import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// KPI configuration — role-based, QUARTER-scoped KPIs (Master Data → KPI).
//
// A parallel surface to Employee Score, deliberately NOT folded into it: KPIs run
// on a quarter axis, target managers by role, and can be genuinely not-applicable
// (a manager with no exits has no attrition ratio). The page has three tabs:
//
//   Configure   → pick a role, choose metrics, set weights + slabs per quarter.
//   Manual Data → upload / inline-edit the values the system can't derive.
//   Scores      → the computed KPI table for a quarter, with per-metric drill-down.
//
// Everything metric-descriptive — explainer text, the tuning-knob declarations,
// availability reasons — comes from the API catalog, so this file renders it
// rather than carrying its own copy (the scorecard duplicates that text into web
// AND mobile; the KPI framework serves it once).
//
// code-guardian-ignore-duplicates: this page is a deliberate parallel of
// EmployeeScorePage — its View/Manage toggle and slab-editor table are kept the same
// as Employee Score's on purpose, so those JSX blocks mirror that page's.

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ChevronDown, ChevronRight, ChevronUp, AlertTriangle, Upload, Download, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { kpiAPI, employeeCustomFieldsAPI, storeCustomFieldsAPI } from '../services/api';
import { downloadXlsx } from '../utils/download';
import { InlineLoader } from '../components/loading';

// ── Types (mirror the API payloads) ─────────────────────────────────────────

interface ConfigParamSpec {
  key: string;
  label: string;
  type: 'enum' | 'boolean' | 'number';
  options?: string[];
  default: string | boolean | number;
  help: string;
}

/** 'custom_field': measured from an employee or store custom field. */
type MetricKind = 'system' | 'manual' | 'custom_field';

/** Which custom field a 'custom_field' metric is built from, and the order of its values. */
interface CustomFieldSource {
  scope: 'employee' | 'store';
  field_id: string;
  measure: 'moved_up';
  /** Lowest first; null for a number field (higher is up). */
  ladder: string[] | null;
}

interface CatalogEntry {
  metric_def_id: number;
  project_id: number | null;
  code: string;
  kind: MetricKind;
  /** kind 'custom_field' only. */
  source_params?: CustomFieldSource | null;
  source_label?: string | null;
  name: string;
  unit: string | null;
  higher_is_better: boolean;
  period_grain: 'quarter' | 'month' | 'rolling_12m';
  upload_grain: 'quarter' | 'month' | null;
  aggregation: 'avg' | 'sum' | 'latest' | null;
  explainer: { summary: string; details: string } | null;
  config_params: ConfigParamSpec[];
  requires_reportees: boolean;
  reason?: string; // present only on `unavailable` entries
}

interface RoleOption {
  id: string;
  name: string;
  user_count: number;
  configured: boolean;
}

interface SlabRow {
  lower_bound: number | null;
  upper_bound: number | null;
  points: number;
  display_label?: string | null;
  display_order?: number;
}

// A metric as it sits in the editing draft: the catalog entry plus its per-config
// weight, slabs, knobs and active/paused state.
interface DraftMetric {
  metric_def_id: number;
  code: string;
  name: string;
  unit: string | null;
  kind: MetricKind;
  higher_is_better: boolean;
  config_params: Record<string, unknown> | null;
  status: 'active' | 'inactive';
  weight_pct: number;
  scoring_mode: ScoringMode;
  /** The version this row came from (quarter start, YYYY-MM-DD). When it differs
   *  from the quarter being edited, the metric is INHERITED — configured in an
   *  earlier quarter and still in force here because effective-dating resolves
   *  per metric, not per version. Empty for a metric added in this session. */
  effective_from: string;
  slabs: SlabRow[];
}

/** How a metric turns its value into points. 'slabs' runs it through the ladder
 *  below; 'raw' has no ladder — the value IS the percentage achieved. */
type ScoringMode = 'slabs' | 'raw';

interface ScoreMetric {
  code: string;
  name: string;
  unit: string | null;
  value: number | null;
  points: number | null;
  /** Top band of this metric's own slab ladder — what `points` is scored out of. */
  max_points: number;
  weight_pct: number;
  effective_weight_pct: number;
  contribution: number;
  applicable: boolean;
  sample_size: number;
  sample_hits?: number | null;
  note?: string | null;
  confidence?: 'full' | 'partial' | null;
  explainer?: { summary: string; details: string } | null;
}

interface ScoreRow {
  employee_code: string;
  employee_name: string;
  role_id: string | null;
  total: number | null;
  /** Sum of ALL configured weights (normally 100) — the denominator for KPI %. */
  achievable_weight?: number;
  not_scored: boolean;
  metrics: ScoreMetric[];
}

// 'metric:<code>' is a per-metric tab — exactly like the Employee Score page's
// per-metric Manage tabs, where each configured metric becomes its own tab
// showing a per-employee breakdown (and, for manual metrics, value entry).
type Tab = 'configure' | 'scores' | `metric:${string}`;

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmtNum = (n: number | null | undefined, dp = 1): string =>
  n === null || n === undefined ? '—' : Number(n).toFixed(dp);

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** '2026-04-01' → '2026-Q2'. Falls back to the raw string for anything unparseable
 *  so a label can never render as "NaN-QNaN". */
const quarterLabelOf = (isoDate: string): string => {
  const m = /^(\d{4})-(\d{2})/.exec(String(isoDate || ''));
  if (!m) return String(isoDate || '—');
  return `${m[1]}-Q${Math.floor((Number(m[2]) - 1) / 3) + 1}`;
};

/** Build the last N quarter ids ending at the current quarter, newest first. */
const recentQuarters = (n = 8): string[] => {
  const now = new Date();
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q < 1) { q = 4; y -= 1; }
  }
  return out;
};

// ── Slab editor (shared by every metric row on Configure) ───────────────────

function SlabEditor({
  slabs,
  unit,
  higherIsBetter,
  scoringMode,
  onChange,
  onModeChange,
}: {
  slabs: SlabRow[];
  unit: string | null;
  higherIsBetter: boolean;
  scoringMode: ScoringMode;
  onChange: (next: SlabRow[]) => void;
  onModeChange: (next: ScoringMode) => void;
}) {
  const update = (i: number, patch: Partial<SlabRow>) => {
    const next = slabs.map((s, j) => (j === i ? { ...s, ...patch } : s));
    onChange(next);
  };
  const add = () => onChange([...slabs, { lower_bound: null, upper_bound: null, points: 0, display_label: '' }]);
  const remove = (i: number) => onChange(slabs.filter((_, j) => j !== i));
  const raw = scoringMode === 'raw';

  // Slab editor styled as a replica of the Employee Score page's slab table
  // (Lower / Upper / points / Label / Remove columns), so the two config
  // surfaces are visually identical.
  return (
    <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
      {/* Scoring mode. Some metrics are already a percentage (a compliance %, a
          target achievement %) and banding them adds nothing — the admin just
          wants the number itself to carry the weight. Offering it here, next to
          the ladder it replaces, is what makes "no slab needed" an explicit
          choice rather than an empty table that silently scores zero. */}
      <div className="mb-3 border-b border-gray-200 pb-3">
        <div className="mb-1.5 text-xs font-medium text-gray-600">How is this metric scored?</div>
        <div className="flex flex-col gap-1.5">
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="radio"
              className="mt-0.5"
              checked={!raw}
              onChange={() => onModeChange('slabs')}
            />
            <span>
              <span className="font-medium">Slabs</span> — map the value to points through the bands below.
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="radio"
              className="mt-0.5"
              checked={raw}
              onChange={() => onModeChange('raw')}
            />
            <span>
              <span className="font-medium">Raw value</span> — no slabs. The value itself is the percentage
              achieved.
            </span>
          </label>
        </div>
        {raw && (
          <div className="mt-2 rounded border border-blue-100 bg-blue-50 p-2 text-[11px] text-blue-900">
            Scored out of 100:{' '}
            <strong>
              {higherIsBetter
                ? 'value ÷ 100 × weight'
                : '(100 − value) ÷ 100 × weight'}
            </strong>
            . A value of {higherIsBetter ? '87' : '13'}{unit === '%' ? '%' : ''} on a 20% weight contributes{' '}
            <strong>17.4</strong>.
            {!higherIsBetter && ' Inverted because lower is better here, so a small value scores well.'}
            {' '}Values outside 0–100 are clamped.
          </div>
        )}
      </div>
      {!raw && (
      <>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-medium text-gray-600">Slabs</div>
        <button onClick={add} className="text-xs font-medium text-primary-700 hover:text-primary-900">
          + Add slab
        </button>
      </div>
      <div className="mb-2 text-[11px] text-gray-500">
        Interval is <strong>exclusive-lower, inclusive-upper</strong>; a blank bound is unbounded on that side.
        {!higherIsBetter && ' Lower is better — points should descend as the value rises.'}
      </div>
      {slabs.length === 0 ? (
        <div className="text-xs text-gray-500">
          No slabs yet. Add at least one to give this metric any points.
        </div>
      ) : (
        <table className="min-w-full text-xs">
          <thead className="text-gray-500">
            <tr>
              <th className="px-2 py-1 text-left">Lower {unit ? `${unit} ` : ''}(&gt;)</th>
              <th className="px-2 py-1 text-left">Upper {unit ? `${unit} ` : ''}(≤)</th>
              <th className="px-2 py-1 text-right">Points</th>
              <th className="px-2 py-1 text-left">Label</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((s, i) => (
              <tr key={i} className="border-t border-gray-200">
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={s.lower_bound ?? ''}
                    placeholder="−∞"
                    onChange={(e) => update(i, { lower_bound: e.target.value === '' ? null : Number(e.target.value) })}
                    className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={s.upper_bound ?? ''}
                    placeholder="+∞"
                    onChange={(e) => update(i, { upper_bound: e.target.value === '' ? null : Number(e.target.value) })}
                    className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-2 py-1 text-right">
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={s.points}
                    onChange={(e) => update(i, { points: Number(e.target.value) || 0 })}
                    className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={s.display_label ?? ''}
                    placeholder="e.g. Excellent"
                    onChange={(e) => update(i, { display_label: e.target.value || null })}
                    className="w-40 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-2 py-1 text-center"><TableActionGroup>
                  <TableActionButton action="remove" label="Remove row" onClick={() => remove(i)} />
                </TableActionGroup></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </>
      )}
    </div>
  );
}

// ── Config-param editor (renders whatever knobs the metric declares) ────────

function ConfigParamEditor({
  specs,
  values,
  onChange,
}: {
  specs: ConfigParamSpec[];
  values: Record<string, unknown> | null;
  onChange: (next: Record<string, unknown>) => void;
}) {
  if (specs.length === 0) return null;
  const val = (k: string, dflt: unknown) => (values && k in values ? values[k] : dflt);
  return (
    <div className="mt-2 space-y-2 bg-gray-50 rounded p-2">
      {specs.map((sp) => (
        <div key={sp.key} className="flex items-start gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600">{sp.label}</label>
            <p className="text-[11px] text-gray-400 leading-snug">{sp.help}</p>
          </div>
          {sp.type === 'enum' && (
            <select
              className="border rounded px-2 py-1 text-sm"
              value={String(val(sp.key, sp.default))}
              onChange={(e) => onChange({ ...(values ?? {}), [sp.key]: e.target.value })}
            >
              {sp.options?.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {sp.type === 'boolean' && (
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={Boolean(val(sp.key, sp.default))}
              onChange={(e) => onChange({ ...(values ?? {}), [sp.key]: e.target.checked })}
            />
          )}
          {sp.type === 'number' && (
            <input
              type="number"
              className="w-24 border rounded px-2 py-1 text-sm"
              value={Number(val(sp.key, sp.default))}
              onChange={(e) => onChange({ ...(values ?? {}), [sp.key]: Number(e.target.value) })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function KpiPage() {
  const projectId = useAuthStore((s) => s.selectedProjectId);
  const userName = useAuthStore((s) => (s.user as any)?.userName || (s.user as any)?.employeeCode || '');

  const [tab, setTab] = useState<Tab>('configure');
  // Role is the top-level axis: pick a role, then Configure sets that role's
  // metrics and Scores shows that role's employees. So it lives ABOVE the tabs.
  const [roleId, setRoleId] = useState<string | null>(null);
  const quarters = useMemo(() => recentQuarters(8), []);
  // Default to the CURRENT quarter — configs default to "effective from" the
  // current quarter, so a freshly-set-up KPI's metric tabs + scores are visible
  // straight away. (Defaulting to the previous quarter hid them, since that
  // quarter is before the config's effective_from.) Governs the metric tabs +
  // Scores view; the user can switch to any past quarter.
  const [quarter, setQuarter] = useState<string>(quarters[0]);
  // Where the quarter came from. 'auto' = we seeded it and may replace it when the role
  // changes; 'user' = the viewer picked it and we must not move it under them. An
  // explicit flag, not a heuristic — someone who deliberately picks the current quarter
  // is otherwise indistinguishable from an untouched default.
  const [quarterSource, setQuarterSource] = useState<'auto' | 'user'>('auto');
  // Per-role landing quarter from the catalog. Absent role => project default.
  const [defaultQuarterByRole, setDefaultQuarterByRole] = useState<Record<string, string>>({});
  const [projectDefaultQuarter, setProjectDefaultQuarter] = useState<string | null>(null);

  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [unavailable, setUnavailable] = useState<CatalogEntry[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!projectId) return;
    setLoadingCatalog(true);
    try {
      const res = await kpiAPI.getCatalog(projectId);
      const d = res.data.data;
      setCatalog(d.catalog);
      setUnavailable(d.unavailable);
      setRoles(d.roles);
      setDefaultQuarterByRole(d.default_quarter_by_role ?? {});
      setProjectDefaultQuarter(d.default_quarter ?? null);
      // Land on a role. The page used to open with none selected, so it showed no
      // metric tabs at all until you clicked a pill — and with no role there is also
      // nothing to resolve a quarter against. Prefer the first CONFIGURED role.
      setRoleId((prev) => {
        if (prev) return prev;
        const configured = (d.roles as RoleOption[]).find((r) => r.configured);
        return configured?.id ?? (d.roles as RoleOption[])[0]?.id ?? null;
      });
    } catch (e: any) {
      window.alert(e?.response?.data?.message || 'Failed to load KPI catalog');
    } finally {
      setLoadingCatalog(false);
    }
  }, [projectId]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Apply the landing quarter for the role in view.
  //
  // Guards, each load-bearing:
  //  • quarterSource === 'auto' — once the viewer picks a quarter, never move it.
  //  • `quarter` is NOT in the deps — writing it here must not re-run this effect.
  //  • only applies a quarter the picker actually offers, so a role whose data predates
  //    the 8-quarter window can't strand the select on a value with no option.
  useEffect(() => {
    if (quarterSource !== 'auto') return;
    const target = (roleId && defaultQuarterByRole[roleId]) || projectDefaultQuarter;
    if (target && quarters.includes(target)) setQuarter(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultQuarterByRole, projectDefaultQuarter, roleId, quarterSource, quarters]);

  // The metrics CONFIGURED for the selected role in the viewing quarter — one
  // per-metric tab each, like the Employee Score page. Loaded from the config
  // (lightweight), independent of the heavier per-employee score fetch below.
  const [roleMetrics, setRoleMetrics] = useState<Array<{ metric_def_id: number; code: string; name: string; unit: string | null; kind: MetricKind }>>([]);
  useEffect(() => {
    if (!projectId || !roleId) { setRoleMetrics([]); return; }
    let cancelled = false;
    kpiAPI.getConfig(projectId, quarter, roleId)
      .then((res) => {
        if (cancelled) return;
        const ms = (res.data.data.metrics as any[]).map((m) => ({
          metric_def_id: m.metric_def_id, code: m.code, name: m.name, unit: m.unit, kind: m.kind,
        }));
        setRoleMetrics(ms);
      })
      .catch(() => { if (!cancelled) setRoleMetrics([]); });
    return () => { cancelled = true; };
  }, [projectId, roleId, quarter]);

  // Shared per-employee scores for (role, quarter) — used by every per-metric tab
  // AND the Scores overview, so one fetch feeds them all. Lazy: only fetched once
  // a metric or Scores tab is opened (Configure never needs it).
  const [scoreRows, setScoreRows] = useState<ScoreRow[]>([]);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [failedMetrics, setFailedMetrics] = useState<Array<{ code: string; name: string; reason: string }>>([]);
  const needScores = typeof tab === 'string' && (tab === 'scores' || tab.startsWith('metric:'));
  useEffect(() => {
    // No userName gate: the server derives the viewer from the session token, so
    // a store that hasn't populated userName no longer blocks the fetch.
    if (!projectId || !roleId || !needScores) return;
    let cancelled = false;
    setScoresLoading(true);
    kpiAPI.getScores(projectId, quarter)
      .then((res) => {
        if (cancelled) return;
        const d = res.data.data;
        setScoreRows((d.rows as ScoreRow[]).filter((r) => r.role_id === roleId));
        setFailedMetrics(d.failedMetrics || []);
      })
      .catch((e: any) => { if (!cancelled) window.alert(e?.response?.data?.message || 'Failed to load scores'); })
      .finally(() => { if (!cancelled) setScoresLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, roleId, quarter, needScores]);

  // MANAGE sub-tabs: one per configured metric, then Configure rightmost —
  // matching the Employee Score page. 'scores' is the sole VIEW surface and is
  // therefore NOT in this list.
  const manageTabs = useMemo<[Tab, string][]>(() => [
    ...roleMetrics.map((m) => [`metric:${m.code}` as Tab, m.name] as [Tab, string]),
    ['configure', 'Configure'],
  ], [roleMetrics]);

  // Mode is derived from the active tab, never stored separately — the same
  // approach EmployeeScorePage uses, so the toggle and the tab can't drift.
  const mode: 'view' | 'manage' = tab === 'scores' ? 'view' : 'manage';

  // The metric whose tab is open (null unless tab is 'metric:<code>').
  const activeMetric = useMemo(() => {
    if (typeof tab !== 'string' || !tab.startsWith('metric:')) return null;
    const code = tab.slice('metric:'.length);
    return roleMetrics.find((m) => m.code === code) ?? null;
  }, [tab, roleMetrics]);

  // If the open metric tab's metric disappears (role/quarter changed), fall back
  // to Configure so we never render a dead tab.
  useEffect(() => {
    if (typeof tab === 'string' && tab.startsWith('metric:') && roleMetrics.length > 0 && !activeMetric) {
      setTab('configure');
    }
  }, [tab, activeMetric, roleMetrics.length]);

  // Server-built XLSX of the Scores table for the selected role + quarter.
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!projectId || !roleId) return;
    setDownloading(true);
    try {
      const res = await kpiAPI.downloadScores(projectId, quarter, roleId);
      const roleLabel = (roles.find((r) => r.id === roleId)?.name ?? 'role').replace(/[^a-zA-Z0-9]+/g, '-');
      downloadXlsx(`KPI-${roleLabel}-${quarter}.xlsx`, res.data);
    } catch (e: any) {
      // The error body arrives as a Blob on a blob-typed request — decode it so
      // the user sees the real reason instead of a generic failure.
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try { window.alert(JSON.parse(text).message); return; } catch { /* fall through */ }
      }
      window.alert('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!projectId) {
    return <div className="text-sm text-gray-500">Select a project to configure KPIs.</div>;
  }

  // No outer padding / max-width here: DashboardLayout's <main> already provides
  // px-6 pt-4 pb-6 and the full content width. Adding p-6 + max-w-6xl (as this
  // page originally did) double-padded it and boxed the content into a narrow
  // centred column, visibly out of step with Employee Score and every other page.
  // Mirror EmployeeScorePage exactly: a `space-y-4` stack, `text-2xl` heading.
  return (
    <div className="space-y-4">
      {/* Header row: title + description on the left, Quarter picker + Download
          on the right — the same shape as the Employee Score page's header
          (Month + Download). Quarter governs the metric tabs AND Scores;
          Configure carries its own "Effective from" control. */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">KPI</h1>
          <p className="mt-1 text-sm text-gray-500">
            Define quarterly KPI metrics for any role — the way Employee Score defines behavioural metrics, but
            scored over a quarter. Employees see their KPI on the app home screen.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="text-sm text-gray-600">Quarter</label>
          <select
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            value={quarter}
            // A deliberate pick — from here on, role changes never move it.
            onChange={(e) => { setQuarter(e.target.value); setQuarterSource('user'); }}
          >
            {quarters.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !roleId}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title={roleId ? 'Download this role’s KPI scores for the selected quarter as Excel' : 'Pick a role first'}
          >
            {downloading ? 'Preparing…' : 'Download'}
          </button>
        </div>
      </div>

      {/* Role — the top-level axis, above the tabs: a role's metrics are
          configured and its employees scored, so it governs both modes. The
          View / Manage toggle sits flush right, as on Employee Score. */}
      {loadingCatalog ? (
        <InlineLoader label="Loading roles…" className="flex" />
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Role</span>
          {roles.map((r) => {
            const isActive = roleId === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRoleId(isActive ? null : r.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ring-1 ${
                  isActive
                    ? 'bg-primary-600 text-white ring-primary-600 shadow'
                    : r.configured
                      ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100'
                      : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
                }`}
                title={r.configured ? 'KPI configured for this role' : 'No KPI yet — click to set one up'}
              >
                {r.configured && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={isActive ? 'text-white' : 'text-emerald-700'} aria-hidden>
                    <path d="M2 6.5l2.5 2.5L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                <span>{r.name}</span>
                <span className={`text-[11px] ${isActive ? 'text-white/70' : 'text-gray-400'}`}>({r.user_count})</span>
              </button>
            );
          })}

          {/* View / Manage toggle — flush right, exactly as on Employee Score.
              View = the Scores table; Manage = the per-metric tabs + Configure.
              `mode` is DERIVED from the active tab (not separate state), so the
              two can never disagree. */}
          <div className="ml-auto flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm font-medium">
            {([['view', 'View'], ['manage', 'Manage']] as const).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  if (mode === m) return;
                  setTab(m === 'view' ? 'scores' : (manageTabs[0]?.[0] ?? 'configure'));
                }}
                className={`rounded-md px-3 py-1 transition ${
                  mode === m
                    ? 'bg-white text-primary-700 shadow ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Manage sub-tabs: one per configured metric, then Configure. Hidden in
          View mode, which has a single surface (the Scores table) and so needs
          no tab row — same as Employee Score. */}
      {mode === 'manage' && (
        <div className="flex gap-1 border-b overflow-x-auto">
          {manageTabs.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === k ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'configure' && (
        <ConfigureTab
          projectId={projectId}
          catalog={catalog}
          unavailable={unavailable}
          roleId={roleId}
          onSaved={loadCatalog}
        />
      )}
      {activeMetric && (
        <MetricScoreTab
          projectId={projectId}
          metric={activeMetric}
          explainer={catalog.find((c) => c.code === activeMetric.code)?.explainer ?? null}
          rows={scoreRows}
          loading={scoresLoading}
          quarter={quarter}
          roleId={roleId}
          viewerCode={userName}
        />
      )}
      {tab === 'scores' && (
        <ScoresTab
          rows={scoreRows}
          loading={scoresLoading}
          failed={failedMetrics}
          roleName={roles.find((r) => r.id === roleId)?.name ?? null}
          quarter={quarter}
        />
      )}
    </div>
  );
}

// ── Configure tab ────────────────────────────────────────────────────────────

function ConfigureTab({
  projectId,
  catalog,
  unavailable,
  roleId,
  onSaved,
}: {
  projectId: number;
  catalog: CatalogEntry[];
  unavailable: CatalogEntry[];
  roleId: string | null;
  onSaved: () => void;
}) {
  const cfgQuarters = useMemo(() => recentQuarters(8), []);
  // Config is effective FROM a quarter — default to the current one so a new
  // config takes effect now and forward.
  const [effectiveFrom, setEffectiveFrom] = useState<string>(cfgQuarters[0]);
  const [draft, setDraft] = useState<DraftMetric[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [effectiveQuarter, setEffectiveQuarter] = useState<string>('');
  // Opt-in per save (see full_set in saveKpiConfig). Default OFF so the long-standing
  // "edit one metric from this quarter" save keeps behaving exactly as it always has.
  const [fullSet, setFullSet] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showCreateMetric, setShowCreateMetric] = useState(false);
  const [showCreateFieldMetric, setShowCreateFieldMetric] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!roleId) { setDraft([]); setVersions([]); return; }
    try {
      const res = await kpiAPI.getConfig(projectId, effectiveFrom, roleId);
      const d = res.data.data;
      setVersions(d.versions || []);
      // The quarter start this view resolves to — the yardstick for "is this row
      // this quarter's own, or inherited from an earlier version?".
      setEffectiveQuarter(String(d.effective_quarter || '').slice(0, 10));
      setDraft(
        (d.metrics as any[]).map((m) => ({
          metric_def_id: m.metric_def_id,
          code: m.code,
          name: m.name,
          unit: m.unit,
          kind: m.kind,
          higher_is_better: m.higher_is_better,
          config_params: m.config_params ?? null,
          status: 'active',
          weight_pct: Number(m.weight_pct),
          // Absent on configs saved before raw mode existed — those are all slabs.
          scoring_mode: m.scoring_mode === 'raw' ? 'raw' : 'slabs',
          effective_from: String(m.effective_from || '').slice(0, 10),
          slabs: (m.slabs as any[]).map((s) => ({
            lower_bound: s.lower_bound,
            upper_bound: s.upper_bound,
            points: Number(s.points),
            display_label: s.display_label,
          })),
        })),
      );
      setDirty(false);
    } catch (e: any) {
      window.alert(e?.response?.data?.message || 'Failed to load config');
    }
  }, [projectId, roleId, effectiveFrom]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const inDraft = useMemo(() => new Set(draft.map((d) => d.metric_def_id)), [draft]);
  const addable = catalog.filter((c) => !inDraft.has(c.metric_def_id));

  const activeWeightTotal = round2(
    draft.filter((d) => d.status === 'active').reduce((s, d) => s + (Number(d.weight_pct) || 0), 0),
  );

  // INHERITED = configured in an EARLIER quarter and never re-saved here, so the
  // per-metric as-of resolver still returns it for this quarter. It scores, and its
  // weight counts, exactly like a metric configured here — which is what made two
  // deliberately-different quarters look like one merged config totalling 200%.
  const isInherited = (d: DraftMetric): boolean =>
    !!d.effective_from && !!effectiveQuarter && d.effective_from < effectiveQuarter;
  const inherited = draft.filter((d) => isInherited(d) && d.status === 'active');

  const mutate = (next: DraftMetric[]) => { setDraft(next); setDirty(true); };

  // Retire every inherited metric for THIS quarter. Deliberately sets status
  // 'inactive' rather than dropping the rows: removing a metric from the payload
  // leaves it with no row for this quarter, so the resolver falls straight back to
  // the older version and it returns. An inactive row here is a genuine override —
  // newest-for-this-metric, and filtered out because it is not active. The earlier
  // quarter's version is untouched, so history still scores the way it did.
  const excludeInherited = () => {
    if (!window.confirm(
      `Stop applying ${inherited.length} carried-over metric${inherited.length === 1 ? '' : 's'} from ${quarterLabelOf(effectiveQuarter)} onward?\n\n`
      + `${inherited.map((d) => `• ${d.name}`).join('\n')}\n\n`
      + 'They stay exactly as they are in the earlier quarter — this only stops them counting from this quarter on. '
      + 'Set the remaining weights to total 100, then Save.',
    )) return;
    mutate(draft.map((d) => (isInherited(d) && d.status === 'active' ? { ...d, status: 'inactive' } : d)));
  };

  const addMetric = (c: CatalogEntry) => {
    mutate([
      ...draft,
      {
        metric_def_id: c.metric_def_id,
        code: c.code,
        name: c.name,
        unit: c.unit,
        kind: c.kind,
        higher_is_better: c.higher_is_better,
        config_params: null,
        status: 'active',
        weight_pct: 0,
        scoring_mode: 'slabs',
        effective_from: '', // added in this session — belongs to the quarter being edited
        slabs: [],
      },
    ]);
    setExpanded((prev) => new Set(prev).add(c.metric_def_id));
  };

  // A newly created manual metric: add it to the draft immediately (so the admin
  // can weight + slab it without a round-trip) AND refresh the parent catalog so
  // it appears in the catalog and survives a reload.
  const addCreatedMetric = (m: CreatedManualMetric) => {
    setShowCreateMetric(false);
    setShowCreateFieldMetric(false);
    onSaved(); // reloads the parent catalog (onSaved === parent's loadCatalog)
    mutate([
      ...draft,
      {
        metric_def_id: m.metric_def_id,
        code: m.code,
        name: m.name,
        unit: m.unit,
        kind: m.kind ?? 'manual',
        higher_is_better: m.higher_is_better,
        config_params: null,
        status: 'active',
        weight_pct: 0,
        scoring_mode: 'slabs',
        effective_from: '', // added in this session — belongs to the quarter being edited
        slabs: [],
      },
    ]);
    setExpanded((prev) => new Set(prev).add(m.metric_def_id));
  };

  const patch = (id: number, p: Partial<DraftMetric>) =>
    mutate(draft.map((d) => (d.metric_def_id === id ? { ...d, ...p } : d)));

  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const specFor = (id: number) =>
    catalog.find((c) => c.metric_def_id === id) ?? unavailable.find((c) => c.metric_def_id === id);

  const save = async () => {
    const active = draft.filter((d) => d.status === 'active');
    if (active.length > 0 && Math.abs(activeWeightTotal - 100) > 0.01) {
      window.alert(`Active weights must sum to 100 — currently ${activeWeightTotal}.`);
      return;
    }
    setSaving(true);
    try {
      const res = await kpiAPI.saveConfig(projectId, {
        role_id: roleId,
        effective_from: effectiveFrom,
        metrics: draft.map((d, i) => ({
          metric_def_id: d.metric_def_id,
          weight_pct: d.weight_pct,
          display_order: i,
          status: d.status,
          config_params: d.config_params,
          scoring_mode: d.scoring_mode,
          // Sent regardless of mode; the API drops them for a raw metric rather
          // than persisting a ladder the engine will never read.
          slabs: d.slabs.map((s, j) => ({ ...s, display_order: j })),
        })),
        // Makes this version the COMPLETE set from this quarter on: the API retires
        // anything in force but not listed here, so a metric removed with ✕ actually
        // goes away instead of falling back to its earlier version.
        full_set: fullSet,
      });
      // Name what was retired. A silent "saved" after a full-set save hides the most
      // consequential half of what just happened.
      const excluded = Number(res?.data?.data?.excluded_count) || 0;
      window.alert(
        excluded > 0
          ? `KPI configuration saved. ${excluded} metric${excluded === 1 ? '' : 's'} no longer applies from ${quarterLabelOf(effectiveQuarter)} onward.`
          : 'KPI configuration saved.',
      );
      setDirty(false);
      onSaved();
      loadConfig();
    } catch (e: any) {
      window.alert(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {!roleId ? (
        <div className="text-gray-400 text-sm italic">Pick a role above to configure its KPI metrics.</div>
      ) : (
        <>
          {/* Effective-from quarter + version list */}
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-600">Effective from</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            >
              {cfgQuarters.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
            {versions.length > 0 && (
              <span className="text-xs text-gray-400">
                Saved versions: {versions.map((v) => v.slice(0, 7)).join(', ')}
              </span>
            )}
          </div>

          {/* Weight total banner */}
          {draft.some((d) => d.status === 'active') && (
            <div
              className={`text-sm mb-3 px-3 py-2 rounded ${
                Math.abs(activeWeightTotal - 100) < 0.01 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
              }`}
            >
              Active weight total: <strong>{activeWeightTotal}</strong> / 100
              {Math.abs(activeWeightTotal - 100) >= 0.01 && ' — must equal 100 to save'}
            </div>
          )}

          {/* Carried-over metrics. THE explanation for the most confusing thing this
              screen does: a version overrides only the metrics it names, so a quarter
              configured with a fresh set shows that set PLUS everything still in force
              from earlier — and the weights add up past 100. Without this the two sets
              render identically and it reads as duplication. */}
          {inherited.length > 0 && (
            <div className="text-xs mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <div>
                <strong>
                  {inherited.length} metric{inherited.length === 1 ? '' : 's'} carried over from an earlier quarter
                </strong>{' '}
                — {inherited.map((d) => d.name).join(', ')}.
              </div>
              <div className="mt-1 text-amber-800">
                A version only overrides the metrics it names. Anything configured earlier and not re-saved
                here is still in force for {quarterLabelOf(effectiveQuarter)}, still scores, and still counts
                toward the weight total above.
              </div>
              <button
                onClick={excludeInherited}
                className="mt-2 rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100"
              >
                Stop applying them from {quarterLabelOf(effectiveQuarter)}
              </button>
            </div>
          )}

          {/* Draft metrics */}
          <div className="space-y-2">
            {draft.map((d) => {
              const spec = specFor(d.metric_def_id);
              const isExpanded = expanded.has(d.metric_def_id);
              return (
                <div key={d.metric_def_id} className={`border rounded-lg ${d.status === 'inactive' ? 'opacity-60 bg-gray-50' : 'bg-white'}`}>
                  <div className="flex items-center gap-3 p-3">
                    <button onClick={() => toggleExpand(d.metric_def_id)} className="text-gray-400">
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      {/* Name + unit + direction, matching the Employee Score
                          page's metric header. */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{d.name}</span>
                        {d.unit && <span className="text-xs text-gray-500">· {d.unit}</span>}
                        <span className={`text-xs ${d.higher_is_better ? 'text-emerald-600' : 'text-amber-600'}`}>
                          · {d.higher_is_better ? '↑ higher is better' : '↓ lower is better'}
                        </span>
                        {d.kind === 'manual' && <span className="text-[10px] bg-purple-100 text-purple-600 rounded px-1.5 py-0.5">manual</span>}
                        {d.kind === 'custom_field' && <span className="text-[10px] bg-purple-100 text-purple-600 rounded px-1.5 py-0.5">custom field</span>}
                        {/* The row is not this quarter's own — it resolves to an
                            older version and is in force here only because nothing
                            newer overrides it. */}
                        {isInherited(d) && (
                          <span
                            className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5 py-0.5"
                            title={`Configured in ${quarterLabelOf(d.effective_from)} and never re-saved for ${quarterLabelOf(effectiveQuarter)}, so it still applies here. Untick Active to stop it applying from this quarter on.`}
                          >
                            from {quarterLabelOf(d.effective_from)}
                          </span>
                        )}
                        {/* Visible without expanding: how this metric is scored is
                            as consequential as its weight, and a collapsed raw
                            metric would otherwise look identical to an unfinished
                            one whose admin simply never added any slabs. */}
                        {d.scoring_mode === 'raw' && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">raw value</span>
                        )}
                        {d.scoring_mode !== 'raw' && d.slabs.length === 0 && d.status === 'active' && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5" title="This metric will score 0 for everyone until it has slabs, or is switched to raw value.">
                            no slabs — scores 0
                          </span>
                        )}
                      </div>
                    </div>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={d.status === 'active'}
                        onChange={(e) => patch(d.metric_def_id, { status: e.target.checked ? 'active' : 'inactive' })}
                      />
                      Active
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className="w-16 border rounded px-2 py-1 text-right text-sm"
                        value={d.weight_pct}
                        min={0}
                        max={100}
                        onChange={(e) => patch(d.metric_def_id, { weight_pct: Number(e.target.value) })}
                        disabled={d.status !== 'active'}
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                    <button
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => mutate(draft.filter((x) => x.metric_def_id !== d.metric_def_id))}
                      title="Remove metric"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t pt-2">
                      {/* "How is this calculated?" — same collapsible pattern as the
                          Employee Score page (a <details> card), so the two config
                          surfaces read identically. */}
                      {spec?.explainer && (
                        <details className="mb-2 rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
                          <summary className="cursor-pointer font-medium text-gray-700">How is this calculated?</summary>
                          <p className="mt-2 text-gray-700">{spec.explainer.summary}</p>
                          <p className="mt-1 text-[11px] text-gray-500">{spec.explainer.details}</p>
                        </details>
                      )}
                      {d.kind === 'custom_field' && (() => {
                        const entry = catalog.find((c) => c.metric_def_id === d.metric_def_id);
                        return <CustomFieldExplainer source={entry?.source_params ?? null} label={entry?.source_label ?? null} />;
                      })()}
                      {d.kind === 'manual' && (
                        <details className="mb-2 rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
                          <summary className="cursor-pointer font-medium text-gray-700">How is this calculated?</summary>
                          <p className="mt-2 text-gray-700">
                            A manual metric the system can’t derive — its value comes from what you upload in
                            this metric’s own tab.{d.unit ? ` Measured in ${d.unit}.` : ''} A monthly upload is
                            averaged/summed across the quarter per the metric’s setting;{' '}
                            {d.scoring_mode === 'raw'
                              ? 'that value is then scored directly as a percentage out of 100.'
                              : 'slabs then map that value to points.'}
                          </p>
                        </details>
                      )}
                      {spec && spec.config_params.length > 0 && (
                        <ConfigParamEditor
                          specs={spec.config_params}
                          values={d.config_params}
                          onChange={(next) => patch(d.metric_def_id, { config_params: next })}
                        />
                      )}
                      <SlabEditor
                        slabs={d.slabs}
                        unit={d.unit}
                        higherIsBetter={d.higher_is_better}
                        scoringMode={d.scoring_mode}
                        onChange={(next) => patch(d.metric_def_id, { slabs: next })}
                        onModeChange={(next) => patch(d.metric_def_id, { scoring_mode: next })}
                      />
                      {/* Manual metric values are entered in this metric's own
                          tab (top of the page), matching the Employee Score
                          page — not inline here. */}
                      {d.kind === 'manual' && (
                        <p className="mt-2 text-[11px] text-gray-400">
                          Upload this metric’s values in its own tab (“{d.name}”) at the top of the page.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add metric — existing catalog metrics as pills, plus an inline
              "New manual metric" affordance so an admin can create a custom
              metric right here; its values are then uploaded inside its card. */}
          <div className="mt-3">
            <div className="text-xs font-medium text-gray-500 mb-1">Add a metric</div>
            <div className="flex flex-wrap gap-2">
              {addable.map((c) => (
                <button
                  key={c.metric_def_id}
                  onClick={() => addMetric(c)}
                  className="px-3 py-1.5 rounded-full text-sm border border-dashed border-gray-300 text-gray-600 hover:border-primary-400 flex items-center gap-1"
                >
                  <Plus size={13} /> {c.name}
                </button>
              ))}
              <button
                onClick={() => setShowCreateMetric(true)}
                className="px-3 py-1.5 rounded-full text-sm border border-dashed border-primary-300 text-primary-600 hover:border-primary-500 hover:bg-primary-50 flex items-center gap-1"
                title="Create a custom metric whose value you upload (e.g. Store Front Feedback)"
              >
                <Plus size={13} /> New manual metric
              </button>
              <button
                onClick={() => setShowCreateFieldMetric(true)}
                className="px-3 py-1.5 rounded-full text-sm border border-dashed border-primary-300 text-primary-600 hover:border-primary-500 hover:bg-primary-50 flex items-center gap-1"
                title="Measure a custom field kept for employees or stores (e.g. iPro Academy Level)"
              >
                <Plus size={13} /> From a custom field
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">
              A “manual metric” is one the system can’t derive — add it here, then upload its values inside its card below.
              One “from a custom field” is measured from an employee or store custom field.
            </p>
          </div>

          {/* Unavailable metrics guidance */}
          {unavailable.length > 0 && (
            <div className="mt-4 bg-amber-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-sm font-medium text-amber-700 mb-1">
                <AlertTriangle size={15} /> Not available yet
              </div>
              <ul className="text-xs text-amber-700 space-y-1">
                {unavailable.map((u) => (
                  <li key={u.metric_def_id}>
                    <strong>{u.name}</strong> — {u.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Save */}
          <div className="mt-5">
            {/* Always visible, NOT tucked into the carried-over banner: removing an
                inherited metric with ✕ takes it out of the draft, which makes that
                banner disappear — exactly the moment you still need this, since the
                metric would otherwise resolve back to its earlier version. */}
            <label className="mb-2 flex items-start gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={fullSet}
                onChange={(e) => { setFullSet(e.target.checked); setDirty(true); }}
              />
              <span>
                <span className="font-medium">
                  These are the only metrics from {quarterLabelOf(effectiveQuarter)} onward
                </span>{' '}
                — anything still in force but not listed above stops applying from this quarter.
                Earlier quarters keep scoring exactly as they do now. Leave unticked to change
                just the metrics listed here.
              </span>
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving || !dirty}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save configuration'}
              </button>
              {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
            </div>
          </div>
        </>
      )}

      {showCreateMetric && (
        <CreateManualMetricModal
          projectId={projectId}
          onClose={() => setShowCreateMetric(false)}
          onCreated={addCreatedMetric}
        />
      )}
      {showCreateFieldMetric && (
        <CreateCustomFieldMetricModal
          projectId={projectId}
          onClose={() => setShowCreateFieldMetric(false)}
          onCreated={addCreatedMetric}
        />
      )}
    </div>
  );
}

// ── Manual-value upload block (used inside a manual metric's tab) ────────────
// Template download + spreadsheet upload + an editable grid of eligible
// employees × upload periods, for entering the values the system can't derive.

function ManualUploadBlock({
  projectId,
  metricDefId,
  metricName,
  unit,
  quarter,
}: {
  projectId: number;
  metricDefId: number;
  metricName: string;
  unit: string | null;
  quarter: string;
}) {
  const [grid, setGrid] = useState<Array<{ employee_code: string; employee_name: string; period_start: string; value: number | null; note: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  // Server-loaded values, keyed "code|period" — compared against the live grid
  // to highlight edited rows (the same amber dirty-row cue the Employee Score
  // manual grid uses).
  const [originals, setOriginals] = useState<Map<string, number | null>>(new Map());

  // Distinct upload periods in the quarter (3 months, or 1 for a quarter-grain
  // metric), ascending — these become the value columns.
  const periods = useMemo(
    () => [...new Set(grid.map((r) => r.period_start))].sort(),
    [grid],
  );

  // Pivot: one entry per employee, with an index into `grid` per period so an
  // edit still writes back to the right flat row.
  const byEmployee = useMemo(() => {
    const map = new Map<string, { employee_code: string; employee_name: string; idxByPeriod: Map<string, number>; dirty: boolean }>();
    grid.forEach((r, i) => {
      const e = map.get(r.employee_code) ?? {
        employee_code: r.employee_code,
        employee_name: r.employee_name,
        idxByPeriod: new Map<string, number>(),
        dirty: false,
      };
      e.idxByPeriod.set(r.period_start, i);
      if ((originals.get(`${r.employee_code}|${r.period_start}`) ?? null) !== (r.value ?? null)) e.dirty = true;
      map.set(r.employee_code, e);
    });
    return [...map.values()];
  }, [grid, originals]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kpiAPI.listManualValues(projectId, metricDefId, quarter);
      const rows = res.data.data.rows as Array<{ employee_code: string; period_start: string; value: number | null }>;
      setGrid(res.data.data.rows);
      setOriginals(new Map(rows.map((r) => [`${r.employee_code}|${r.period_start}`, r.value ?? null])));
    } catch {
      setGrid([]);
      setOriginals(new Map());
    } finally {
      setLoading(false);
    }
  }, [projectId, metricDefId, quarter]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const setCell = (idx: number, value: number | null) =>
    setGrid((g) => g.map((r, i) => (i === idx ? { ...r, value } : r)));

  const saveGrid = async () => {
    try {
      const res = await kpiAPI.saveManualValues(
        projectId, metricDefId, quarter,
        grid.map((r) => ({ employee_code: r.employee_code, period_start: r.period_start, value: r.value, note: r.note })),
      );
      const d = res.data.data;
      window.alert(`Saved. ${d.written} value(s) written, ${d.cleared} cleared.`);
      loadGrid();
    } catch (e: any) {
      window.alert(e?.response?.data?.message || 'Save failed');
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await kpiAPI.downloadManualTemplate(projectId, metricDefId, quarter);
      downloadXlsx(`${metricName.replace(/[^a-zA-Z0-9]+/g, '-')}-${quarter}.xlsx`, res.data);
    } catch (e: any) {
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try { window.alert(JSON.parse(text).message); return; } catch { /* fall through */ }
      }
      window.alert('Template download failed — add this metric to a role’s config first.');
    }
  };

  const onUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = (e.target?.result as string).split(',')[1];
        const res = await kpiAPI.bulkUploadManualValues(projectId, metricDefId, quarter, base64);
        const d = res.data.data;
        let msg = `Upload complete: ${d.inserted} new, ${d.updated} updated, ${d.skipped_blank} blank skipped, ${d.rejected_count} rejected.`;
        if (d.rejected_count > 0) {
          msg += '\n\nRejected rows:\n' + d.rejected.slice(0, 20).map((r: any) => `  row ${r.row} (${r.employee_code || 'blank'}): ${r.reason}`).join('\n');
        }
        window.alert(msg);
        loadGrid();
      } catch (err: any) {
        window.alert(err?.response?.data?.message || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">Enter values for {quarter}</span>
        <div className="flex items-center gap-3">
          <button onClick={downloadTemplate} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
            <Download size={13} /> Template
          </button>
          <label className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer">
            <Upload size={13} /> Upload
            <input type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
          </label>
        </div>
      </div>
      {loading ? (
        <InlineLoader label="Loading…" size="xs" className="flex" />
      ) : grid.length === 0 ? (
        <div className="text-gray-400 text-xs italic">
          No eligible employees yet — save this metric in the role’s config (with active members) first.
        </div>
      ) : (
        <>
          {/* ONE ROW PER EMPLOYEE with a column per upload period — the Employee
              Score manual-value grid is one row per employee, and repeating the
              same name once per month (3× for a monthly metric) made this list
              three times longer and much harder to scan. */}
          <div className="border rounded-lg overflow-hidden bg-white">
            <div className="overflow-auto max-h-[55vh]">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 sticky top-0 z-10 bg-gray-50">Employee Name</th>
                    <th className="text-left px-3 py-2 sticky top-0 z-10 bg-gray-50">Employee Code</th>
                    {periods.map((p) => (
                      <th key={p} className="text-right px-3 py-2 whitespace-nowrap sticky top-0 z-10 bg-gray-50">
                        {p.slice(0, 7)}{unit ? ` (${unit})` : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byEmployee.map((emp) => (
                    <tr key={emp.employee_code} className={emp.dirty ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{emp.employee_name}</td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{emp.employee_code}</td>
                      {periods.map((p) => {
                        const idx = emp.idxByPeriod.get(p);
                        const row = idx == null ? null : grid[idx];
                        return (
                          <td key={p} className="px-3 py-2 text-right">
                            <input
                              type="number"
                              inputMode="decimal"
                              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:outline-none"
                              value={row?.value ?? ''}
                              placeholder="—"
                              disabled={idx == null}
                              onChange={(e) => idx != null && setCell(idx, e.target.value.trim() === '' ? null : Number(e.target.value))}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={saveGrid} className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium">Save values</button>
            <p className="text-[11px] text-gray-400">A blank cell clears the value back to “not uploaded” (scored as N/A, not 0).</p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Per-metric tab ───────────────────────────────────────────────────────────
// One metric's own tab, matching the Employee Score page's per-metric Manage
// tabs: a "How is this calculated?" card, a per-employee breakdown table
// (value / points / weight / weighted, with the note showing e.g. how many
// stores were visited of how many handled), and — for manual metrics — the
// value-upload block. Reads the shared per-employee score rows.

function MetricScoreTab({
  projectId,
  metric,
  explainer,
  rows,
  loading,
  quarter,
  roleId,
  viewerCode,
}: {
  projectId: number;
  metric: { metric_def_id: number; code: string; name: string; unit: string | null; kind: MetricKind };
  explainer: { summary: string; details: string } | null;
  rows: ScoreRow[];
  loading: boolean;
  quarter: string;
  roleId: string | null;
  viewerCode: string;
}) {
  // One entry per employee: this metric's slice of their row.
  const perEmployee = useMemo(
    () => rows.map((r) => ({ row: r, m: r.metrics.find((x) => x.code === metric.code) })).filter((e) => e.m),
    [rows, metric.code],
  );

  // SECTION STRIP (the third tab level), matching SubMetricsManagePanel's
  // sub-metric → Result → Per-employee scores strip on the Employee Score page.
  //
  // The value-entry grid used to be STACKED above the scores table, so a manual
  // metric rendered two unrelated full-width tables on one screen — input and
  // output competing for the same space. That is the same mistake the scorecard
  // made and fixed (see the comment on its Scores tab); this strip is that fix.
  //
  // Order is inputs/evidence first, computed score last — except for a system
  // metric with an evidence view, where the score IS the primary surface and the
  // audit trail is secondary. The default is always the leftmost tab.
  const sections = useMemo(() => {
    const out: Array<{ key: 'values' | 'exits' | 'cohort' | 'scores'; label: string; badge?: string }> = [];
    if (metric.kind === 'manual') out.push({ key: 'values', label: 'Enter values', badge: 'upload' });
    out.push({ key: 'scores', label: 'Per-employee scores', badge: 'slabs × weight' });
    if (metric.code === 'infant_attrition_pct') {
      out.push({ key: 'exits', label: 'Exits considered', badge: 'numerator' });
      out.push({ key: 'cohort', label: 'Team in first year', badge: 'denominator' });
    }
    return out;
  }, [metric.kind, metric.code]);

  const [section, setSection] = useState<'values' | 'exits' | 'cohort' | 'scores'>(sections[0].key);
  // Switching metric tabs remounts with a different section list; snap back to
  // the leftmost so we can never land on a section this metric doesn't have.
  useEffect(() => {
    if (!sections.some((s) => s.key === section)) setSection(sections[0].key);
  }, [sections, section]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {metric.name}{metric.unit ? ` · ${metric.unit}` : ''}
            {metric.kind === 'manual' && <span className="ml-2 text-[10px] bg-purple-100 text-purple-600 rounded px-1.5 py-0.5">manual</span>}
            {metric.kind === 'custom_field' && <span className="ml-2 text-[10px] bg-purple-100 text-purple-600 rounded px-1.5 py-0.5">custom field</span>}
          </div>
          <div className="text-xs text-gray-400">Per-employee breakdown for {quarter}.</div>
        </div>
      </div>

      {explainer && (
        <details className="rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
          <summary className="cursor-pointer font-medium text-gray-700">How is this calculated?</summary>
          <p className="mt-2 text-gray-700">{explainer.summary}</p>
          <p className="mt-1 text-[11px] text-gray-500">{explainer.details}</p>
        </details>
      )}

      {/* Only worth a strip when there's more than one surface to switch between. */}
      {sections.length > 1 && (
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap gap-1">
            {sections.map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                className={`relative inline-flex items-center gap-2 px-3 py-2 text-sm transition ${
                  section === s.key ? 'text-primary-700 font-semibold' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <span>{s.label}</span>
                {s.badge && (
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700">
                    {s.badge}
                  </span>
                )}
                {section === s.key && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-600" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Manual metrics: the value-entry block (system metrics are auto-derived). */}
      {section === 'values' && metric.kind === 'manual' && (
        <ManualUploadBlock
          projectId={projectId}
          metricDefId={metric.metric_def_id}
          metricName={metric.name}
          unit={metric.unit}
          quarter={quarter}
        />
      )}

      {(section === 'exits' || section === 'cohort') && (
        <MetricExitsSection
          view={section}
          projectId={projectId}
          quarter={quarter}
          metricCode={metric.code}
          roleId={roleId}
          viewerCode={viewerCode}
        />
      )}

      {section === 'scores' && (
      <>
      {/* Per-employee scores for this metric — like the Employee Score page's
          per-metric table (Raw value / Points / Weighted), plus a Detail column
          that shows the note, e.g. "2 of 4 handled stores visited". */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Name</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Code</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Detail</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Value{metric.unit ? ` (${metric.unit})` : ''}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Points</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Weight</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Weighted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></td></tr>
              ) : perEmployee.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                  No employees for this role/quarter. Ensure a KPI is configured for this role and the quarter is on or after its “Effective from”.
                </td></tr>
              ) : (
                perEmployee.map(({ row, m }) => (
                  <tr key={row.employee_code}>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{row.employee_name}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{row.employee_code}</td>
                    {/* Compact numerator/denominator ratio (e.g. "45 / 73" =
                        stores visited / handled). Falls back to the note when
                        there's no ratio (N/A rows, manual metrics). The full note
                        is on hover. */}
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap" title={m!.note ?? ''}>
                      {m!.sample_hits != null && m!.sample_size
                        ? `${m!.sample_hits} / ${m!.sample_size}`
                        : (m!.note || '—')}
                      {m!.confidence === 'partial' && (
                        <span className="ml-1 text-[10px] bg-amber-100 text-amber-600 rounded px-1">reduced confidence</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900">
                      {m!.applicable ? fmtNum(m!.value, 1) : <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {m!.applicable ? (
                        <>
                          {fmtNum(m!.points, 0)}
                          <span className="text-gray-400">{` / ${fmtNum(m!.max_points, 0)}`}</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{fmtNum(m!.weight_pct, 0)}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-primary-700">
                      {m!.applicable ? fmtNum(m!.contribution, 1) : '0.0'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// ── Exits behind the attrition metric ────────────────────────────────────────
// The audit surface: every exit the quarter's infant-attrition value was built
// from, with joining date, last working day and tenure — plus the exits that were
// DROPPED and why. An admin asking "are the right people being counted?" cannot
// answer it from a list that has already been filtered, so this shows both.

const fmtTenure = (days: number | null): string => {
  if (days === null) return '—';
  // A 30-day month fits twelve times before 365: 360–364 days is a year, never "12m".
  const rest = Math.floor((days % 365) / 30);
  const y = Math.floor(days / 365) + (rest === 12 ? 1 : 0);
  const m = rest === 12 ? 0 : rest;
  if (y <= 0 && m <= 0) return `${days}d`;
  return `${y > 0 ? `${y}y ` : ''}${m}m (${days}d)`;
};

interface CohortRow {
  employee_code: string;
  employee_name: string;
  doj: string;
  /** Set when they left inside the quarter; null = still on roll. */
  lwd: string | null;
  tenure_days: number;
  /** True when this person is also in the numerator. */
  left_early: boolean;
  charged_to_names: string[];
}

interface ExitRow {
  employee_code: string;
  employee_name: string;
  role_name: string | null;
  doj: string | null;
  lwd: string;
  tenure_days: number | null;
  is_infant: boolean | null;
  manager_code: string | null;
  manager_name: string | null;
  /** The scored manager(s) the exit was charged to — often an ancestor of the
   *  direct manager, e.g. an ISR under a TL rolling up to the AM. */
  charged_to_names: string[];
  counted: boolean;
  excluded_reason: string | null;
  source: string;
}

function MetricExitsSection({
  projectId,
  quarter,
  metricCode,
  roleId,
  viewerCode,
  view,
}: {
  projectId: number;
  quarter: string;
  metricCode: string;
  roleId: string | null;
  viewerCode: string;
  /** Which side of the ratio to render. Both arrive in ONE fetch, so switching
   *  tabs is instant and the two lists can never be from different requests. */
  view: 'exits' | 'cohort';
}) {
  const [exits, setExits] = useState<ExitRow[]>([]);
  const [cohort, setCohort] = useState<CohortRow[]>([]);
  const [summary, setSummary] = useState<{ total_exits: number; counted: number; excluded: number; infant: number; unclassified: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!viewerCode) return;
      setLoading(true);
      setError(null);
      // Clear BEFORE refetching. Otherwise the previous quarter's chips ("12
      // exits in 2026-Q2 / 9 counted") stay on screen relabelled with the new
      // quarter while the request is in flight, and survive indefinitely if it
      // fails — a surface whose entire job is verifying a number must never
      // assert counts for a period it did not load.
      setExits([]);
      setCohort([]);
      setSummary(null);
      try {
        const res = await kpiAPI.getMetricEvidence(projectId, quarter, metricCode, viewerCode, roleId);
        if (cancelled) return;
        setExits(res.data?.data?.exits ?? []);
        setCohort(res.data?.data?.cohort ?? []);
        setSummary(res.data?.data?.summary ?? null);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load exits');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, quarter, metricCode, roleId, viewerCode]);

  return (
    <div className="space-y-3">
      {view === 'cohort' && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700">{cohort.length} in their first year</span>
          <span className="rounded-full bg-red-50 px-2.5 py-1 font-medium text-red-700">{cohort.filter((c) => c.left_early).length} of them left</span>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">{cohort.filter((c) => !c.left_early).length} still here</span>
        </div>
      )}
      {summary && view === 'exits' && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700">{summary.total_exits} exits in {quarter}</span>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">{summary.counted} counted</span>
          <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700">{summary.infant} under 1 year</span>
          {summary.unclassified > 0 && (
            <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700">{summary.unclassified} unclassified</span>
          )}
          {summary.excluded > 0 && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-500">{summary.excluded} excluded</span>
          )}
        </div>
      )}

      {view === 'cohort' ? (
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Name</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Code</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Date of Joining</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Tenure</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Status</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Counted For</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-red-600">{error}</td></tr>
              ) : cohort.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500">
                  Nobody was inside their first year during {quarter}, so there is no denominator and the metric scores 0%.
                </td></tr>
              ) : (
                cohort.map((c) => (
                  <tr key={c.employee_code} className={c.left_early ? 'bg-red-50/40' : ''}>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{c.employee_name}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{c.employee_code}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{c.doj}</td>
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">{fmtTenure(c.tenure_days)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.left_early ? (
                        <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5">left early</span>
                      ) : c.lwd ? (
                        <span className="text-[10px] bg-gray-200 text-gray-600 rounded px-1.5 py-0.5">left after 1 year</span>
                      ) : (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">still here</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{c.charged_to_names.join(', ')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Name</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Employee Code</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Role</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">DOJ</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Last Working Day</th>
                <th className="whitespace-nowrap px-3 py-2 text-right sticky top-0 z-20 bg-gray-50">Tenure</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Under 1 Year</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">Counted Against</th>
                <th className="whitespace-nowrap px-3 py-2 text-left sticky top-0 z-20 bg-gray-50">In Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></td></tr>
              ) : error ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-red-600">{error}</td></tr>
              ) : exits.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-gray-500">
                  No team exits recorded in {quarter}. Every manager for this role is therefore scored N/A on this metric.
                </td></tr>
              ) : (
                exits.map((e) => (
                  <tr key={`${e.employee_code}-${e.lwd}`} className={e.counted ? '' : 'bg-gray-50/60 text-gray-400'}>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{e.employee_name}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{e.employee_code}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{e.role_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {e.doj || <span className="text-amber-600" title="No joining date was ever captured, so this exit counts in the denominator but can never be classified as early.">not on record</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{e.lwd}</td>
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">{fmtTenure(e.tenure_days)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {e.is_infant === null ? (
                        <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">unknown</span>
                      ) : e.is_infant ? (
                        <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5">Yes</span>
                      ) : (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {e.charged_to_names?.length ? (
                        <>
                          <div>{e.charged_to_names.join(', ')}</div>
                          {/* Show the direct manager only when it DIFFERS, so the
                              roll-up is visible without adding noise to the common
                              case where they're the same person. */}
                          {e.manager_name && !e.charged_to_names.includes(e.manager_name) && (
                            <div className="text-[11px] text-gray-400">via {e.manager_name}</div>
                          )}
                        </>
                      ) : (
                        e.manager_name || '—'
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" title={e.excluded_reason ?? ''}>
                      {e.counted
                        ? <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">counted</span>
                        : <span className="text-[10px] bg-gray-200 text-gray-600 rounded px-1.5 py-0.5">excluded</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      {view === 'cohort' ? (
      <p className="text-[11px] text-gray-400">
        These are the people the rate is divided BY — everyone in your reporting line still inside their first 365 days
        at some point in the quarter. Only they can leave “early”, so only they belong in the denominator. A highlighted
        row left early, so they appear on both sides of the ratio.
      </p>
      ) : (
      <p className="text-[11px] text-gray-400">
        Tenure is Last Working Day minus Date of Joining; under 365 days counts toward the Attrition Rate. Both dates come from
        the monthly roster snapshot, which freezes them while the employee is still active — the live directory drops a
        departed employee’s manager link entirely. An exit is charged to every scored manager whose reporting line
        contains it, so an ISR under a Team Lead rolls up to the Area Manager above them — “via” names the direct
        manager when it differs. An “excluded” row reached nobody; hover it for the reason.
      </p>
      )}
    </div>
  );
}

// The created metric, echoed back so a caller (the Configure tab) can add it to
// its draft without waiting for a catalog refresh round-trip.
interface CreatedManualMetric {
  metric_def_id: number;
  code: string;
  name: string;
  unit: string | null;
  higher_is_better: boolean;
  /** Omitted for a manual metric. */
  kind?: MetricKind;
}

/** What a custom-field metric measures, under its card on Configure. */
function CustomFieldExplainer({ source, label }: { source: CustomFieldSource | null; label: string | null }) {
  const subjects = source?.scope === 'store' ? 'stores they handle' : 'people under them';
  return (
    <details className="mb-2 rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
      <summary className="cursor-pointer font-medium text-gray-700">How is this calculated?</summary>
      <p className="mt-2 text-gray-700">
        Built from the {source?.scope === 'store' ? 'store' : 'employee'} custom field{label ? ` “${label}”` : ''}: the share of the{' '}
        {subjects} whose value moved up between the start and the end of the quarter.
      </p>
      {source?.ladder ? (
        <p className="mt-1 text-[11px] text-gray-500">
          Lowest to highest: {source.ladder.join(' → ')}. Anyone already at the top is left out, and so is anyone whose value
          is not on this list. Values are recorded from the day this measure was switched on.
        </p>
      ) : (
        <p className="mt-1 text-[11px] text-gray-500">
          A higher number counts as moving up. Values are recorded from the day this measure was switched on.
        </p>
      )}
    </details>
  );
}

/** One value of a dropdown field, as the ladder editor holds it. */
interface LadderStep { value: string; isLevel: boolean }

export function CreateCustomFieldMetricModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: number;
  onClose: () => void;
  onCreated: (created: CreatedManualMetric) => void;
}) {
  const [scope, setScope] = useState<'employee' | 'store'>('employee');
  const [fields, setFields] = useState<Array<{ id: string; label: string; fieldType: string; options: string[] | null }>>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [fieldId, setFieldId] = useState('');
  const [ladder, setLadder] = useState<LadderStep[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingFields(true);
    setFieldId('');
    setLadder([]);
    const read = scope === 'employee' ? employeeCustomFieldsAPI.list(projectId) : storeCustomFieldsAPI.list(projectId);
    read
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.data ?? []) as Array<{ id: string; label: string; fieldType: string; options: string[] | null; active: boolean }>;
        setFields(list.filter((f) => f.active !== false && (f.fieldType === 'dropdown' || f.fieldType === 'number')));
      })
      .catch(() => { if (!cancelled) setFields([]); })
      .finally(() => { if (!cancelled) setLoadingFields(false); });
    return () => { cancelled = true; };
  }, [projectId, scope]);

  const field = fields.find((f) => f.id === fieldId) ?? null;
  const pickField = (id: string) => {
    setFieldId(id);
    const f = fields.find((x) => x.id === id);
    setLadder(f?.fieldType === 'dropdown' ? (f.options ?? []).map((value) => ({ value, isLevel: true })) : []);
    if (f) setName((prev) => prev || `${f.label} moved up`);
  };
  const move = (i: number, by: -1 | 1) => setLadder((prev) => {
    const j = i + by;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const levels = ladder.filter((s) => s.isLevel).map((s) => s.value);

  const create = async () => {
    setError(null);
    if (!name.trim()) { setError('Give the metric a name.'); return; }
    if (!field) { setError('Pick a field.'); return; }
    if (field.fieldType === 'dropdown' && levels.length < 2) { setError('Keep at least two values as levels.'); return; }
    setSaving(true);
    try {
      const res = await kpiAPI.createManualMetric(projectId, {
        kind: 'custom_field',
        name: name.trim(),
        scope,
        field_id: field.id,
        measure: 'moved_up',
        ladder: field.fieldType === 'dropdown' ? levels : null,
      });
      const d = res.data.data;
      onCreated({
        metric_def_id: Number(d.metric_def_id),
        code: String(d.code),
        name: String(d.name),
        unit: d.unit ?? '%',
        higher_is_better: true,
        kind: 'custom_field',
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create metric');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-5 w-[28rem] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-800 mb-1">Metric from a custom field</h3>
        <p className="text-xs text-gray-500 mb-3">
          Each manager scores the share of their team (or the stores they handle) whose value moved up during the quarter.
        </p>
        <label className="block text-xs text-gray-500 mb-1">Kept for</label>
        <select className="w-full border rounded px-2 py-1 mb-3" value={scope} onChange={(e) => setScope(e.target.value as 'employee' | 'store')}>
          <option value="employee">Employees</option>
          <option value="store">Stores</option>
        </select>
        <label className="block text-xs text-gray-500 mb-1">Field</label>
        {loadingFields ? (
          <div className="mb-3 text-xs text-gray-400">Loading fields…</div>
        ) : fields.length === 0 ? (
          <div className="mb-3 text-xs text-gray-500">No dropdown or number field is kept for {scope === 'employee' ? 'employees' : 'stores'} in this project.</div>
        ) : (
          <select className="w-full border rounded px-2 py-1 mb-3" value={fieldId} onChange={(e) => pickField(e.target.value)}>
            <option value="">Pick a field</option>
            {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        )}
        {field?.fieldType === 'dropdown' && (
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1">Levels, lowest first</div>
            <ul className="divide-y divide-gray-100 rounded border border-gray-200">
              {ladder.map((s, i) => (
                <li key={s.value} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={s.isLevel}
                    onChange={(e) => setLadder((prev) => prev.map((x, k) => (k === i ? { ...x, isLevel: e.target.checked } : x)))}
                    aria-label={`${s.value} is a level`}
                  />
                  <span className={`flex-1 ${s.isLevel ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{s.value}</span>
                  <button type="button" className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${s.value} towards the lowest`}>
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" disabled={i === ladder.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${s.value} towards the highest`}>
                    <ChevronDown size={14} />
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-gray-400">
              Untick a value that is not a level (for example “not found”): anyone on it is left out. Anyone at the top level is
              left out too, as they cannot move up.
            </p>
          </div>
        )}
        {field?.fieldType === 'number' && (
          <p className="mb-3 text-[11px] text-gray-500">A higher number counts as moving up.</p>
        )}
        <label className="block text-xs text-gray-500 mb-1">Name</label>
        <input className="w-full border rounded px-2 py-1 mb-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. iPro Academy Level moved up" />
        <p className="text-[11px] text-gray-400 mb-3">
          Values are recorded from the day this was switched on, so a quarter already under way is measured from then.
        </p>
        {error && <p className="mb-2 text-xs text-rose-600" role="alert">{error}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-500">Cancel</button>
          <button onClick={create} disabled={saving || !field} className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50">
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateManualMetricModal({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: number;
  onClose: () => void;
  onCreated: (created: CreatedManualMetric) => void;
}) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [higherIsBetter, setHigherIsBetter] = useState(true);
  const [uploadGrain, setUploadGrain] = useState<'month' | 'quarter'>('month');
  const [aggregation, setAggregation] = useState<'avg' | 'sum' | 'latest'>('avg');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { window.alert('Name is required'); return; }
    setSaving(true);
    try {
      const res = await kpiAPI.createManualMetric(projectId, {
        name: name.trim(), unit: unit.trim() || null, higher_is_better: higherIsBetter, upload_grain: uploadGrain, aggregation,
      });
      const d = res.data.data;
      onCreated({
        metric_def_id: Number(d.metric_def_id),
        code: String(d.code),
        name: String(d.name),
        unit: d.unit ?? null,
        higher_is_better: higherIsBetter,
      });
    } catch (e: any) {
      window.alert(e?.response?.data?.message || 'Failed to create metric');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-5 w-96" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-800 mb-3">New manual metric</h3>
        <label className="block text-xs text-gray-500 mb-1">Name</label>
        <input className="w-full border rounded px-2 py-1 mb-3" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Store Front Feedback" />
        <label className="block text-xs text-gray-500 mb-1">Unit (optional)</label>
        <input className="w-full border rounded px-2 py-1 mb-3" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. /5" />
        <label className="block text-xs text-gray-500 mb-1">Direction</label>
        <select className="w-full border rounded px-2 py-1 mb-3" value={higherIsBetter ? 'higher' : 'lower'} onChange={(e) => setHigherIsBetter(e.target.value === 'higher')}>
          <option value="higher">Higher is better</option>
          <option value="lower">Lower is better</option>
        </select>
        <label className="block text-xs text-gray-500 mb-1">Uploaded</label>
        <select className="w-full border rounded px-2 py-1 mb-3" value={uploadGrain} onChange={(e) => setUploadGrain(e.target.value as any)}>
          <option value="month">Monthly (averaged across the quarter)</option>
          <option value="quarter">Once per quarter</option>
        </select>
        {uploadGrain === 'month' && (
          <>
            <label className="block text-xs text-gray-500 mb-1">Combine months by</label>
            <select className="w-full border rounded px-2 py-1 mb-3" value={aggregation} onChange={(e) => setAggregation(e.target.value as any)}>
              <option value="avg">Average</option>
              <option value="sum">Sum</option>
              <option value="latest">Latest</option>
            </select>
          </>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-500">Cancel</button>
          <button onClick={create} disabled={saving} className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50">
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Scores tab ───────────────────────────────────────────────────────────────

function ScoresTab({
  rows,
  loading,
  failed,
  roleName,
  quarter,
}: {
  rows: ScoreRow[];
  loading: boolean;
  failed: Array<{ code: string; name: string; reason: string }>;
  roleName: string | null;
  quarter: string;
}) {
  // Metric columns = the union of metrics across the visible rows, in first-seen
  // order (every row for a role carries the same set). Drives the per-metric
  // columns in the score table, like the Employee Score page.
  const metricColumns = useMemo(() => {
    const seen = new Map<string, { code: string; name: string; unit: string | null }>();
    for (const r of rows) {
      for (const m of r.metrics) {
        if (!seen.has(m.code)) seen.set(m.code, { code: m.code, name: m.name, unit: m.unit });
      }
    }
    return [...seen.values()];
  }, [rows]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        {roleName && <span className="text-xs text-gray-500">Role: <strong>{roleName}</strong> · {quarter}</span>}
        <span className="text-xs text-gray-400">Prefers the nightly snapshot; falls back to a live compute.</span>
      </div>

      {failed.length > 0 && (
        <div className="bg-red-50 text-red-700 text-xs rounded p-2 mb-3">
          Some metrics failed to compute: {failed.map((f) => `${f.name} (${f.reason})`).join('; ')}
        </div>
      )}

      {loading ? (
        <InlineLoader label="Loading…" className="flex" />
      ) : rows.length === 0 ? (
        <div className="text-gray-400 text-sm italic">
          {roleName
            ? `No KPI scores for ${roleName} in this quarter. Check that a KPI is configured for this role and that this quarter is on or after its "Effective from" quarter.`
            : 'Pick a role above to see its KPI scores, or no role in your hierarchy has a KPI configured for this quarter.'}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            {/* Flat table: every metric's Value / Points / Weight / Contribution
                are their own columns (grouped under the metric name) — no
                expand/collapse. The metric `note` (e.g. "45 of 73 stores
                visited") is a hover tooltip on the Value cell. */}
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500">
                <tr className="uppercase tracking-wide">
                  <th rowSpan={2} className="whitespace-nowrap px-3 py-2 text-left align-bottom sticky top-0 left-0 z-30 bg-gray-50">Employee Name</th>
                  <th rowSpan={2} className="whitespace-nowrap px-3 py-2 text-left align-bottom sticky top-0 z-20 bg-gray-50">Employee Code</th>
                  {metricColumns.map((c) => (
                    <th key={c.code} colSpan={4} className="whitespace-nowrap px-3 py-1.5 text-center border-l border-gray-200 sticky top-0 z-20 bg-gray-100 text-gray-600">
                      {c.name}{c.unit ? ` (${c.unit})` : ''}
                    </th>
                  ))}
                  <th rowSpan={2} className="whitespace-nowrap px-3 py-2 text-right align-bottom sticky top-0 z-20 bg-gray-50">Total</th>
                  <th rowSpan={2} className="whitespace-nowrap px-3 py-2 text-right align-bottom sticky top-0 z-20 bg-gray-50">KPI %</th>
                </tr>
                <tr className="normal-case">
                  {metricColumns.map((c) => (
                    <Fragment key={c.code}>
                      <th className="whitespace-nowrap px-3 py-1.5 text-right font-normal border-l border-gray-200 sticky top-[33px] z-10 bg-gray-50">Value</th>
                      <th className="whitespace-nowrap px-3 py-1.5 text-right font-normal sticky top-[33px] z-10 bg-gray-50">Points</th>
                      <th className="whitespace-nowrap px-3 py-1.5 text-right font-normal sticky top-[33px] z-10 bg-gray-50">Weight</th>
                      <th className="whitespace-nowrap px-3 py-1.5 text-right font-normal sticky top-[33px] z-10 bg-gray-50">Contribution</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.employee_code} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white">{r.employee_name}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{r.employee_code}</td>
                    {metricColumns.map((c) => {
                      const m = r.metrics.find((x) => x.code === c.code);
                      const na = !m || !m.applicable;
                      return (
                        <Fragment key={c.code}>
                          <td
                            className="px-3 py-2 text-right text-gray-900 whitespace-nowrap border-l border-gray-100"
                            title={m?.note ?? ''}
                          >
                            {na ? <span className="text-gray-400">N/A</span> : fmtNum(m!.value, 1)}
                            {m?.confidence === 'partial' && (
                              <span className="ml-1 text-[9px] bg-amber-100 text-amber-600 rounded px-1 align-middle" title="reduced confidence">~</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                            {na ? (
                              '—'
                            ) : (
                              <>
                                {fmtNum(m!.points, 0)}
                                <span className="text-gray-400">{` / ${fmtNum(m!.max_points, 0)}`}</span>
                              </>
                            )}
                          </td>
                          {/* An N/A metric keeps its CONFIGURED weight and simply
                              contributes 0 — weights are never rescaled. */}
                          <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">{m ? `${fmtNum(m.weight_pct, 0)}%` : '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">{na ? '0.0' : fmtNum(m!.contribution, 1)}</td>
                        </Fragment>
                      );
                    })}
                    <td className="px-3 py-2 text-right text-gray-700">
                      {r.not_scored ? <span className="text-gray-400 text-xs">not scored</span> : fmtNum(r.total, 1)}
                    </td>
                    {/* The headline number: total ÷ full configured weight.
                        Identical to Total whenever the weights sum to 100. */}
                    <td className="px-3 py-2 text-right font-semibold text-primary-700">
                      {r.total == null || !r.achievable_weight
                        ? <span className="text-gray-400 text-xs">—</span>
                        : `${fmtNum((r.total / r.achievable_weight) * 100, 1)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.some((r) => r.metrics.some((m) => !m.applicable)) && (
            <div className="px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
              Points are scored as a share of that metric’s own slab ladder — “4 / 4” is full marks and earns the whole weight, so the KPI % doesn’t change if you rebuild a ladder on a different point scale. Contribution is (Points ÷ ladder top) × Weight. “N/A” means the metric had no data for that manager (e.g. no stores handled, no values uploaded); it still counts at its configured weight and scores 0, so every total is out of the full 100. Hover a Value to see its detail (e.g. stores visited).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
